import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mic, MicOff, Send, Sparkles, StopCircle, Volume2, VolumeX, Square, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ChangeModeModal } from "@/components/PracticeModeModal";
import { getPersonaPhoto } from "@/lib/personaPhoto";

export default function Session() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // mode: "voice" | "text" — passed from Scenarios page via navigation state
  const initialMode = location.state?.mode || localStorage.getItem("leadersim_practice_mode") || "voice";

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [input, setInput] = useState("");
  const [voiceMode, setVoiceMode] = useState(initialMode === "voice");
  const [audioOn, setAudioOn] = useState(initialMode === "voice"); // TTS on only for voice mode
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [showChangeMode, setShowChangeMode] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const lastSpokenIdRef = useRef(null);

  // Load session
  useEffect(() => {
    api.get(`/sessions/${id}`)
      .then((res) => setSession(res.data))
      .catch((err) => {
        toast.error(formatApiError(err));
        navigate("/scenarios");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Auto-scroll transcript
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [session?.messages?.length]);

  // Speak the latest assistant message (only in voice mode, only the most recent, once)
  useEffect(() => {
    if (!audioOn || !voiceMode || !session) return;
    const assistantMsgs = session.messages.filter((m) => m.role === "assistant");
    const last = assistantMsgs[assistantMsgs.length - 1];
    if (!last) return;
    const sig = `${last.created_at}:${last.content.slice(0, 32)}`;
    if (lastSpokenIdRef.current === sig) return;
    lastSpokenIdRef.current = sig;
    speakText(last.content, session.scenario?.voice || "coral");
  }, [session?.messages?.length, audioOn]);

  const speakText = async (text, voice) => {
    try {
      const res = await api.post("/voice/tts", { text, voice, session_id: session?.id });
      const src = `data:${res.data.mime};base64,${res.data.audio_base64}`;
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const a = new Audio(src);
      audioRef.current = a;
      a.play().catch(() => {});
    } catch (err) {
      // Silent fail; audio is non-blocking
      console.warn("TTS failed", err);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const sendMessage = async (text, isVoice = false) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setSending(true);
    setInput("");
    // Optimistic add
    setSession((s) =>
      s
        ? {
            ...s,
            messages: [
              ...s.messages,
              { role: "user", content, created_at: new Date().toISOString() },
            ],
          }
        : s,
    );
    try {
      const res = await api.post(`/sessions/${id}/messages`, { content, is_voice: isVoice });
      setSession(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        await handleAudioUpload(blob);
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
      // Stop the AI playing so user can think
      stopAudio();
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const handleAudioUpload = async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "audio.webm");
      const res = await api.post("/voice/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (res.data?.text || "").trim();
      if (text) {
        await sendMessage(text, true);
      } else {
        toast("No speech detected — try again");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTranscribing(false);
    }
  };

  const endSession = async () => {
    setEndingSession(true);
    try {
      await api.post(`/sessions/${id}/end`);
      navigate(`/session/${id}/review`);
    } catch (err) {
      toast.error(formatApiError(err));
      setEndingSession(false);
    }
  };

  if (loading || !session) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20 text-stone-500 text-sm">Loading session…</div>
      </div>
    );
  }

  const { scenario, messages } = session;
  const transcriptMessages = messages.filter((m) => m.role !== "coach");
  const coachHints = messages.filter((m) => m.role === "coach");

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT - Persona context */}
          <aside className="lg:col-span-3 flex flex-col gap-4">
            <div className="bg-white border border-stone-200 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={getPersonaPhoto(scenario.persona_name, scenario.persona_gender)}
                  alt={scenario.persona_name}
                  data-testid="session-persona-photo"
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-sm border border-stone-100 shrink-0"
                />
                <div>
                  <div className="font-display font-medium text-stone-900" data-testid="session-persona-name">{scenario.persona_name}</div>
                  <div className="text-xs text-stone-500">{scenario.persona_role}</div>
                </div>
              </div>
              <div className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 inline-block mb-3">
                {scenario.persona_mood}
              </div>
              <div className="text-sm text-stone-600 leading-relaxed">{scenario.description}</div>
            </div>

            <div className="bg-white border border-stone-200 rounded-3xl p-6">
              <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 mb-3">Your objectives</div>
              <ul className="space-y-2.5">
                {scenario.objectives.map((o, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-stone-700">
                    <span className="w-5 h-5 rounded-full bg-red-50 text-[#b81f24] grid place-items-center text-[11px] font-medium shrink-0 mt-0.5">{i + 1}</span>
                    <span className="leading-snug">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* CENTER - Conversation */}
          <main className="lg:col-span-6 flex flex-col bg-white border border-stone-200 rounded-3xl overflow-hidden min-h-[70vh] lg:h-[calc(100vh-7rem)]">
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={getPersonaPhoto(scenario.persona_name, scenario.persona_gender)}
                  alt={scenario.persona_name}
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm border border-stone-100 shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">Role-play</div>
                  <div className="font-display text-base text-stone-900 truncate">{scenario.title}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${audioOn ? "bg-[#b81f24]" : "bg-stone-300"}`} />
                <span className="text-xs text-stone-500">{session.status === "active" ? "Live" : "Ended"}</span>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4" data-testid="transcript">
              {transcriptMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} slide-up-fade`}>
                  <div
                    className={
                      m.role === "user"
                        ? "bg-[#b81f24] text-white px-5 py-3 rounded-3xl rounded-tr-sm max-w-[85%] text-[15px] leading-relaxed"
                        : "bg-stone-100 text-stone-800 px-5 py-3 rounded-3xl rounded-tl-sm max-w-[85%] text-[15px] leading-relaxed"
                    }
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-stone-100 text-stone-500 px-5 py-3 rounded-3xl rounded-tl-sm text-sm flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400 speaking-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400 speaking-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-stone-400 speaking-pulse" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-stone-100 p-4 bg-stone-50/50">
              <div className="flex items-end gap-3">
                {/* Mic button — visible only in voice mode */}
                {voiceMode ? (
                  <button
                    type="button"
                    data-testid="push-to-talk-btn"
                    onClick={recording ? stopRecording : startRecording}
                    disabled={sending || transcribing || session.status !== "active"}
                    className={`w-16 h-16 rounded-full grid place-items-center shrink-0 transition-all ${
                      recording
                        ? "bg-red-600 text-white voice-pulse"
                        : "bg-orange-600 text-white hover:bg-orange-700 hover:scale-105 active:scale-95"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    aria-label={recording ? "Stop recording" : "Start recording"}
                  >
                    {recording ? <StopCircle className="w-7 h-7" strokeWidth={1.5} /> : <Mic className="w-7 h-7" strokeWidth={1.5} />}
                  </button>
                ) : null}
                <div className="flex-1">
                  <Textarea
                    data-testid="message-input"
                    placeholder={
                      recording ? "Listening…" :
                      transcribing ? "Transcribing…" :
                      voiceMode ? "Type or press the mic to speak" : "Type your response…"
                    }
                    value={input}
                    disabled={recording || transcribing || sending || session.status !== "active"}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    rows={2}
                    className="resize-none rounded-2xl bg-white border-stone-200 focus-visible:ring-[#b81f24]/20 focus-visible:border-[#b81f24]"
                  />
                </div>
                <Button
                  data-testid="send-message-btn"
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || sending || session.status !== "active"}
                  className="rounded-full bg-[#b81f24] hover:bg-[#9a1a1f] text-white h-12 w-12 p-0 shrink-0"
                >
                  <Send className="w-4 h-4" strokeWidth={1.75} />
                </Button>
              </div>
              <div className="mt-2 text-[11px] text-stone-400 ml-[76px]">
                {voiceMode
                  ? "Enter to send · Shift+Enter for new line · Hold the mic for voice"
                  : "Enter to send · Shift+Enter for new line"}
              </div>
            </div>
          </main>

          {/* RIGHT - Coaching hints */}
          <aside className="lg:col-span-3 flex flex-col gap-4">
            <div className="bg-white border border-stone-200 rounded-3xl p-6">
              <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500 mb-1">Live coach</div>
              <div className="font-display text-base text-stone-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" strokeWidth={1.75} />
                Hints as you go
              </div>
            </div>

            <div className="space-y-3 max-h-[calc(100vh-15rem)] overflow-y-auto pr-1" data-testid="coach-hints-panel">
              {coachHints.length === 0 ? (
                <div className="bg-stone-50 border border-dashed border-stone-200 text-stone-500 rounded-2xl p-5 text-sm">
                  Tips will appear here after your first message — small nudges to keep you on track.
                </div>
              ) : (
                coachHints.slice().reverse().map((c, i) => (
                  <div
                    key={i}
                    data-testid="coach-hint"
                    className="bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-4 text-sm leading-relaxed slide-up-fade"
                  >
                    <div className="flex gap-2 items-start">
                      <Sparkles className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
                      <span>{c.content}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Floating bottom-center controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40" data-testid="session-controls-bar">
        <div className="flex items-center gap-2 bg-white/90 backdrop-blur-md border border-stone-200 shadow-xl shadow-stone-900/10 rounded-full px-2 py-2">
          {/* Voice on/off — only shown in voice mode */}
          {voiceMode && (
            <Button
              data-testid="toggle-audio-btn"
              variant="outline"
              onClick={() => {
                setAudioOn((v) => !v);
                stopAudio();
              }}
              className="rounded-full border-stone-200 bg-white hover:bg-stone-100 px-4 h-11"
            >
              {audioOn ? <Volume2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> : <VolumeX className="w-4 h-4 mr-1.5" strokeWidth={1.5} />}
              {audioOn ? "Voice on" : "Voice off"}
            </Button>
          )}

          {/* Change practice mode */}
          <Button
            data-testid="change-mode-btn"
            variant="outline"
            onClick={() => setShowChangeMode(true)}
            className="rounded-full border-stone-200 bg-white hover:bg-stone-100 px-4 h-11 text-stone-600"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
            <span className="text-xs">{voiceMode ? "Voice" : "Text"}</span>
          </Button>

          <Button
            data-testid="end-session-btn"
            onClick={endSession}
            disabled={endingSession}
            className="rounded-full bg-[#b81f24] hover:bg-[#9a1a1f] text-white px-5 h-11"
          >
            <Square className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
            {endingSession ? "Evaluating…" : "End session"}
          </Button>
        </div>
      </div>

      {/* Change Practice Mode modal */}
      {showChangeMode && (
        <ChangeModeModal
          currentMode={voiceMode ? "voice" : "text"}
          onConfirm={(newMode) => {
            const isVoice = newMode === "voice";
            setVoiceMode(isVoice);
            setAudioOn(isVoice);
            if (!isVoice) stopAudio(); // stop any playing audio when switching to text
            setShowChangeMode(false);
            toast.success(`Switched to ${isVoice ? "Voice" : "Text"} mode`);
          }}
          onClose={() => setShowChangeMode(false)}
        />
      )}
    </div>
  );
}
